import { useNavigate } from "@tanstack/react-router";
import { descripcionCrear, indexDePaso, mapearEstadoAStep, urlPorPaso } from "@/components/solicitudes/stepper/state-mapping";
import { stepsParaTipo, TERMINAL_NEGATIVOS, type PasoSolicitud } from "@/components/solicitudes/stepper/steps-config";
import { Card } from "../Card";
import { EstadoTerminalAlert } from "./EstadoTerminalAlert";
import { VerticalStepper } from "./VerticalStepper";

export type { PasoSolicitud } from "@/components/solicitudes/stepper/steps-config";

export interface SolicitudStepperProps {
  estado: string | null;
  subtipo: string | null;
  tipo?: string | null;
  paso?: PasoSolicitud;
  solicitudId?: string;
  contabilizada?: boolean;
  fillHeight?: boolean;
  motivos?: string[];
  dadaDeBajaPorNombre?: string | null;
  esperaCfdiConciliadoAt?: string | null;
}

/**
 * Reemplazo de `components/solicitudes/solicitud-stepper.tsx` (Mantine
 * `<Stepper>`) · misma lógica de resolución de paso activo — se reusa
 * `stepper/steps-config.tsx` y `stepper/state-mapping.ts` TAL CUAL (son
 * lógica pura, sin Mantine) — solo el render cambia, sobre `VerticalStepper`
 * (Etapa 4) en vez del hack CSS de `highlight-styles.ts`.
 */
export function SolicitudStepper({
  estado,
  subtipo,
  tipo,
  paso,
  solicitudId,
  contabilizada = false,
  fillHeight = false,
  motivos,
  dadaDeBajaPorNombre,
  esperaCfdiConciliadoAt = null,
}: SolicitudStepperProps) {
  const navigate = useNavigate();
  const esMultiple = subtipo === "MULTIPLES_CFDI";
  const esContrapago = subtipo === "CONTRAPAGO";
  const esInvoice = subtipo === "INVOICE";
  const esTerminalNegativo = estado !== null && TERMINAL_NEGATIVOS.has(estado);

  if (esTerminalNegativo) {
    return <EstadoTerminalAlert estado={estado!} motivos={motivos} dadaDeBajaPorNombre={dadaDeBajaPorNombre} />;
  }

  const steps = stepsParaTipo(tipo, subtipo).map((s) => {
    if (s.key === "crear") return { ...s, description: descripcionCrear(tipo, subtipo) };
    if (s.key === "cfdi" && esMultiple) return { ...s, label: "ZIP de CFDIs", description: "Carga y validación F6" };
    if (s.key === "cfdi" && esInvoice) return { ...s, label: "Factura (PDF)", description: "Sube el PDF · se extrae por OCR" };
    return s;
  });

  const contabIdx = steps.findIndex((s) => s.key === "contabilizacion");

  const activeIndex = paso !== undefined ? indexDePaso(paso, steps) : mapearEstadoAStep(estado, tipo, steps, subtipo, esperaCfdiConciliadoAt);
  const estadoIndex = mapearEstadoAStep(estado, tipo, steps, subtipo, esperaCfdiConciliadoAt);
  const maxAccesibleIndex = Math.max(activeIndex, estadoIndex);

  function esClickable(idx: number): boolean {
    if (!solicitudId) return false;
    if (idx === activeIndex) return false;
    if (idx === 0) return false;
    if (idx === contabIdx && contabilizada) return true;
    return idx <= maxAccesibleIndex;
  }

  function handleStepClick(idx: number) {
    if (!solicitudId || !esClickable(idx)) return;
    const url = urlPorPaso(steps[idx]?.key, solicitudId, esMultiple, esContrapago, esInvoice);
    if (url) void navigate({ to: url as never });
  }

  return (
    <Card padding="md" className={fillHeight ? "h-full overflow-visible" : "overflow-visible"}>
      <VerticalStepper steps={steps} activeIndex={Math.max(0, activeIndex)} maxAccesibleIndex={maxAccesibleIndex} isClickable={esClickable} onStepClick={handleStepClick} />
    </Card>
  );
}
